import React, { useEffect, useState } from 'react';
import { Field, FieldProps, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormCheckboxInput } from '@tanium/react-form-checkbox-input';
import { useTheme } from '@tanium/react-theme-context';
import { FormSection, FormRow } from '@tanium/react-form';
import { Box } from '@tanium/react-box';
import { useSelector } from 'react-redux';
import RequestAccessLevelConfig from 'gql/RequestAccessLevelConfig';
import IApprovalLevel from 'common/IApprovalLevel';
import { CreateRequestForm } from 'components/pages/request/CreateRequestPage';
import { Modal } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';
import { Button } from '@tanium/react-button';
import RequestSelectors from '../../store/RequestSelectors';

export const AUTO_PAGE = 'autopage';

export const RequestPagingInfo = (): React.ReactElement => {
  const { t } = useTranslation();
  const formik = useFormikContext<CreateRequestForm>();
  const accessLevelConfig = useSelector(RequestSelectors.selectRequestAccessLevelConfig);
  const theme = useTheme();
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  useEffect(() => {
    // If is an auto-approval event
    if (isAutoApprove(accessLevelConfig)) {
      setIsDisabled(true);
    }
  }, [accessLevelConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsDisabled(!formik.values.accessTypes?.length);
  }, [formik.values.accessTypes]);

  useEffect(() => {
    if (isDisabled) {
      void formik.setFieldValue(AUTO_PAGE, false);
    }
  }, [isDisabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAutoApprove = (config: RequestAccessLevelConfig | undefined) => {
    return config && config.accessLevel === IApprovalLevel.AutoApprovalLevel;
  };

  return (
    <FormSection title={t('NOTIFICATIONS')}>
      <Box>
        <Field name={AUTO_PAGE} data-testid={AUTO_PAGE}>
          {({ field }: FieldProps): React.ReactElement => (
            <FormRow
              label={t('PAGERDUTY')}
              description={field.value && <Box color={theme.colors.warning}>{t('PAGE_WARNING')}</Box>}
            >
              <Modal
                isOpen={modalOpen}
                header={t('PAGE_REQUEST_APPROVER_MODAL_HEADER')}
                footer={
                  <Stack inline itemSpacing={1}>
                    <Button
                      data-testid="confirm-cancel"
                      variant="secondary"
                      onClick={() => {
                        void formik.setFieldValue(AUTO_PAGE, false);
                        setModalOpen(false);
                      }}
                    >
                      {t('CANCEL')}
                    </Button>
                    <Button
                      data-testid="confirm-page-request-approver"
                      variant="primary"
                      type="submit"
                      onClick={() => setModalOpen(false)}
                    >
                      {t('PAGE_REQUEST_APPROVER_MODAL_BUTTON')}
                    </Button>
                  </Stack>
                }
              >
                <p>
                  {t('PAGE_REQUEST_CONFIRMATION')}
                  {/* If autopage is on (ie, it is outside business hours), display the business hours message */}
                  {accessLevelConfig && !accessLevelConfig.willPage && t('PAGE_REQUEST_CONFIRMATION_BUSINESS_HOURS')}
                </p>
              </Modal>
              <FormCheckboxInput
                data-testid={field.name}
                {...field}
                label={t('PAGE_APPROVERS')}
                name={field.name}
                id={field.name}
                checked={field.value}
                disabled={isDisabled}
                onChangeCapture={() => setModalOpen(!field.value)}
              />
            </FormRow>
          )}
        </Field>
      </Box>
    </FormSection>
  );
};

export default RequestPagingInfo;
